import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { ShipmentService } from '../../../data/shipment.service';
import { combineLatest, Observable } from 'rxjs';
import { shipmentQuotingProgress } from '../../../store/quote-progress/quote-progress.state';
import { map } from 'rxjs/operators';
import { ShipmentStatusEnum } from '../../../enum/shipment-status.enum';
import { AsyncPipe } from '@angular/common';
import { MatBadge } from '@angular/material/badge';

@Component({
    selector: 'app-shipment-indicator',
    templateUrl: './shipment-indicator.component.html',
    styleUrls: ['./shipment-indicator.component.scss'],
    imports: [MatBadge, AsyncPipe]
})
export class ShipmentIndicatorComponent implements OnInit {

    @Input()
    id: string;

    @Input()
    defaultState: string;

    status$: Observable<string>;
    isQuotingInProgress$: Observable<boolean>;
    isQuotingCompleted$: Observable<boolean>;
    isQuote$: Observable<boolean>;
    isQuoted$: Observable<boolean>;
    isManifested$: Observable<boolean>;
    isInTransit$: Observable<boolean>;
    isDelivered$: Observable<boolean>;
    isVoid$: Observable<boolean>;
    isException$: Observable<boolean>;
    statusText$: Observable<string>;
    progress$: Observable<number>;
    messageCount$: Observable<number>;

    constructor(private store: Store<AppState>, private shipmentService: ShipmentService) {
    }

    ngOnInit(): void {
        this.progress$ = this.store.select(shipmentQuotingProgress(this.id)).pipe(
            map((progress) => progress.progress)
        );
        this.messageCount$ = this.store.select(shipmentQuotingProgress(this.id)).pipe(
            map((progress) => (progress?.messages?.length) ? progress.messages.length : null)
        );
        this.statusText$ = combineLatest([
            this.store.select(shipmentQuotingProgress(this.id)),
            this.shipmentService.observe(this.id)
        ]).pipe(
            map(([ progress, shipment ]) => {
                if (shipment && shipment.statusCode !== ShipmentStatusEnum.QUOTE) {
                    return shipment.statusText;
                }
                if (progress) {
                    if (progress.messages.length) {
                        return progress.messages.join('<br/>');
                    }
                }
                return null;
            })
        );
        this.status$ = combineLatest([
            this.store.select(shipmentQuotingProgress(this.id)),
            this.shipmentService.observe(this.id)
        ]).pipe(
            map(([ progress, shipment ]) => {
                if (shipment && shipment.statusCode !== ShipmentStatusEnum.QUOTE) {
                    return shipment.statusCode;
                }
                if (progress && progress.busy) {
                    return ShipmentStatusEnum.QUOTING_IN_PROGRESS;
                }
                if (progress && progress.queued) {
                    return ShipmentStatusEnum.QUOTING_IN_PROGRESS;
                }
                if (progress && shipment.preSelectedQuoteId) {
                    return ShipmentStatusEnum.QUOTING_COMPLETED;
                }
                if (shipment) {
                    return shipment.statusCode;
                }
                return this.defaultState;
            })
        );
        this.isQuotingInProgress$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.QUOTING_IN_PROGRESS)
        );
        this.isQuotingCompleted$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.QUOTING_COMPLETED)
        );
        this.isQuote$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.QUOTE || status === ShipmentStatusEnum.PREPARING)
        );
        this.isManifested$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.MANIFESTED)
        );
        this.isInTransit$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.IN_TRANSIT)
        );
        this.isDelivered$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.DELIVERED)
        );
        this.isVoid$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.VOID)
        );
        this.isException$ = this.status$.pipe(
            map(status => status === ShipmentStatusEnum.EXCEPTION)
        );
    }

}
